import { useCallback } from "react";
import { useSearchParams } from "react-router";

/**
 * A filter value kept in the page URL (?key=value) instead of component state.
 *
 * Inside a work tab the URL is the tab's own location, so filters are per tab,
 * survive switching tabs (as all page state does), and also survive a browser
 * reload and can be shared as a link. The default value is left out of the URL
 * to keep links short. History is replaced, not pushed: typing in a search box
 * must not fill the back button with one entry per keystroke.
 *
 * Changing a filter goes back to the first page of a paged list (?page is
 * dropped in the same update; two updates in one tick would undo each other).
 * Keys that only open something on top of the list keep the page.
 */
const KEEPS_PAGE = new Set(["page", "sku", "case", "wave", "mode"]);

export function useUrlFilter<T extends string>(key: string, fallback: T, allowed?: readonly T[]): [T, (v: T) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);
  const value = raw !== null && (!allowed || (allowed as readonly string[]).includes(raw)) ? (raw as T) : fallback;

  const set = useCallback(
    (next: T) =>
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          if (next === fallback || next === "") out.delete(key);
          else out.set(key, next);
          if (!KEEPS_PAGE.has(key)) out.delete("page");
          return out;
        },
        { replace: true },
      ),
    [setParams, key, fallback],
  );
  return [value, set];
}

/** Sets several URL filters in one update (empty removes); drops ?page too. */
export function useSetUrlFilters() {
  const [, setParams] = useSearchParams();
  return useCallback(
    (updates: Record<string, string>) =>
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v === "") out.delete(k);
            else out.set(k, v);
          }
          out.delete("page");
          return out;
        },
        { replace: true },
      ),
    [setParams],
  );
}
